import { FC } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type LabelProps,
  type TooltipContentProps,
} from 'recharts';
import { fmtMedida, type LinhaUf, type Medida } from '@/utils/equiplexVendas';
import { fmtPct } from '@/utils/painelFormat';
import { ALTURA_BARRA_HORIZONTAL, FONTE_ROTULO, tickEixo, type CoresPainel } from '../chartTheme';
import PainelCard from '../PainelCard';
import TooltipBox from '../TooltipBox';

interface Props {
  testId: string;
  titulo: string;
  subtitulo: string;
  linhas: LinhaUf[];
  medida: Medida;
  vazio: string;
  cores: CoresPainel;
}

/** Valor e participação na ponta da barra — a barra tem uma cor só, o texto diz o número. */
function rotuloPonta(linhas: LinhaUf[], medida: Medida, cor: string) {
  const RotuloPonta = (props: LabelProps) => {
    const { x, y, width, height, index } = props;
    const l = typeof index === 'number' ? linhas[index] : undefined;
    if (!l || typeof x !== 'number' || typeof y !== 'number') return null;
    const fim = x + (typeof width === 'number' ? width : 0);
    const meio = y + (typeof height === 'number' ? height : 0) / 2;
    return (
      <text x={fim + 6} y={meio} dominantBaseline="central" fill={cor} fontSize={FONTE_ROTULO}>
        <tspan fontWeight={600}>{fmtMedida(l.valor, medida)}</tspan> · {fmtPct(l.share)}
      </text>
    );
  };
  return RotuloPonta;
}

/** Faturado por UF de destino (top 10 + "Outras"), numa cor só: o ranking é a informação. */
const UfChart: FC<Props> = ({ testId, titulo, subtitulo, linhas, medida, vazio, cores }) => {
  const conteudoTooltip = ({ active, payload }: TooltipContentProps) => {
    const l = active && payload?.[0] ? (payload[0].payload as LinhaUf) : null;
    if (!l) return null;
    return (
      <TooltipBox
        titulo={l.nome}
        linhas={[
          { rotulo: 'Faturado', valor: fmtMedida(l.valor, medida, false), cor: cores.faturado },
          { rotulo: 'Participação', valor: fmtPct(l.share) },
        ]}
      />
    );
  };

  return (
    <PainelCard testId={testId} titulo={titulo} subtitulo={subtitulo}>
      {linhas.length === 0 ? (
        <div className="py-10 text-center text-sm text-content-muted">{vazio}</div>
      ) : (
        <div style={{ width: '100%', height: Math.max(180, linhas.length * ALTURA_BARRA_HORIZONTAL + 16) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={linhas} layout="vertical" margin={{ top: 0, right: 132, bottom: 0, left: 8 }} barCategoryGap={8}>
              <CartesianGrid horizontal={false} stroke={cores.grade} />
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="rotulo" width={90} tick={tickEixo(cores)} axisLine={false} tickLine={false} />
              <Tooltip content={conteudoTooltip} cursor={{ fill: cores.grade, fillOpacity: 0.35 }} />
              <Bar dataKey="valor" name="Faturado" fill={cores.faturado} radius={[0, 4, 4, 0]} maxBarSize={24} isAnimationActive={false}>
                <LabelList dataKey="valor" content={rotuloPonta(linhas, medida, cores.texto)} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </PainelCard>
  );
};

export default UfChart;
